import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';
import { toPng } from 'html-to-image';
import { config } from '../../config';

export default function MesCartes() {
  const [cartes, setCartes] = useState([]);
  const [moyensPaiement, setMoyensPaiement] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrImages, setQrImages] = useState({});
  const [faceVisible, setFaceVisible] = useState({});
  const face1Refs = useRef({});
  const face2Refs = useRef({});

  // Utilisation de la configuration centralisée
  const API_BASE = config.apiBase;
  const getToken = () => localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Générer un QR code à partir du code PIN
  const generateQRCode = async (codePin, carteId) => {
    try {
      const qrDataUrl = await QRCode.toDataURL(codePin, {
        width: 80,
        margin: 1,
        color: {
          dark: '#1e3a5f',
          light: '#ffffff'
        }
      });
      setQrImages(prev => ({ ...prev, [carteId]: qrDataUrl }));
    } catch (error) {
      console.error('Erreur génération QR:', error);
    }
  };

  // Récupérer les cartes de l'étudiant
  const fetchCartes = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/etudiant/cartes/get_mes_cartes.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCartes(data.cartes);
        data.cartes.forEach(carte => {
          if (carte.code_pin) {
            generateQRCode(carte.code_pin, carte.id_carte);
          }
        });
      }
    } catch (error) {
      console.error('Erreur fetch cartes:', error);
      toast.error('Erreur de chargement des cartes');
    }
  };

  // Récupérer les moyens de paiement
  const fetchMoyensPaiement = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/etudiant/moyens_paiement/get_moyens_paiement.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setMoyensPaiement((data.moyens || []).map(mp => ({
          ...mp,
          qr_image_url: config.normalizeQrCode(mp.qr_image_url)
        }))); 
      }
    } catch (error) {
      console.error('Erreur chargement moyens paiement:', error);
    }
  };

  // Basculer entre les faces
  const toggleFace = (carteId) => {
    setFaceVisible(prev => ({ ...prev, [carteId]: !prev[carteId] }));
  };

  // Télécharger une face de la carte
  const downloadFace = async (carteId, face, carteNom) => {
    const ref = face === 1 ? face1Refs.current[carteId] : face2Refs.current[carteId];
    if (!ref) return;
    
    try {
      const dataUrl = await toPng(ref, {
        quality: 1,
        pixelRatio: 3,
        backgroundColor: null,
        cacheBust: true,
      });
      
      const link = document.createElement('a');
      link.download = `carte_${carteNom}_face${face}.png`;
      link.href = dataUrl;
      link.click();
      
      toast.success(`Face ${face} téléchargée avec succès`);
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Télécharger les deux faces
  const downloadBothFaces = async (carteId, carteNom) => {
    toast.loading('Préparation des images...', { id: 'download' });
    
    try {
      const ref1 = face1Refs.current[carteId];
      if (ref1) {
        const dataUrl1 = await toPng(ref1, { quality: 1, pixelRatio: 3, backgroundColor: null, cacheBust: true });
        const link1 = document.createElement('a');
        link1.download = `carte_${carteNom}_face1.png`;
        link1.href = dataUrl1;
        link1.click();
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      const ref2 = face2Refs.current[carteId];
      if (ref2) {
        const dataUrl2 = await toPng(ref2, { quality: 1, pixelRatio: 3, backgroundColor: null, cacheBust: true });
        const link2 = document.createElement('a');
        link2.download = `carte_${carteNom}_face2.png`;
        link2.href = dataUrl2;
        link2.click();
      }
      
      toast.success('Les deux faces ont été téléchargées avec succès', { id: 'download' });
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      toast.error('Erreur lors du téléchargement', { id: 'download' });
    }
  };

  // Obtenir le moyen de paiement correspondant à l'institution de la carte
  const getMoyenPaiementForCarte = (carte) => {
    const institutionCarte = carte.institution;
    if (!institutionCarte || !moyensPaiement.length) return null;
    return moyensPaiement.find(mp => mp.institution === institutionCarte && mp.statut === 'actif');
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchCartes(), fetchMoyensPaiement()]);
      setLoading(false);
    };
    init();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de vos cartes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">💳 Mes cartes</h1>
        <p className="text-gray-500">Gérez vos cartes et moyens de paiement</p>
      </div>

      {cartes.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <div className="text-5xl mb-4">💳</div>
          <p className="text-gray-500">Vous n'avez aucune carte associée</p>
          <p className="text-sm text-gray-400 mt-2">Contactez l'administration pour obtenir une carte</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {cartes.map(carte => {
            const estFace1 = !faceVisible[carte.id_carte];
            const moyenPaiement = getMoyenPaiementForCarte(carte);
            const carteNom = `${carte.nom_etudiant || currentUser.nom}_${carte.id_carte}`.replace(/\s/g, '_');
            
            return (
              <div key={carte.id_carte} className="relative" style={{ perspective: '1000px' }}>
                <div 
                  className="relative w-full transition-transform duration-500"
                  style={{ 
                    transformStyle: 'preserve-3d',
                    transform: !estFace1 ? 'rotateY(180deg)' : 'none'
                  }}
                >
                  
                  {/* FACE 1 - Recto - Format carte bancaire standard */}
                  <div 
                    ref={el => face1Refs.current[carte.id_carte] = el}
                    className="backface-hidden"
                    style={{ backfaceVisibility: 'hidden' }}
                  >
                    <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg shadow-xl overflow-hidden relative" style={{ aspectRatio: '1.585 / 1', maxWidth: '340px', margin: '0 auto', width: '100%' }}>
                      {/* Puce */}
                      <div className="absolute top-2 right-2">
                        <div className="w-7 h-5 bg-yellow-400 rounded opacity-80"></div>
                      </div>
                      
                      <div className="p-2.5">
                        {/* Logo */}
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="text-white/50 text-[6px] tracking-wider uppercase">CARTE ÉTUDIANTE</div>
                            <div className="text-white font-bold text-[9px]">CROUS DU NIGER</div>
                          </div>
                          <div className="text-white/30 text-sm">🏦</div>
                        </div>
                        
                        {/* Institution */}
                        <div className="mb-2">
                          <div className="text-white/40 text-[5px] uppercase tracking-wider">Institution</div>
                          <div className="text-white font-semibold text-[8px] bg-white/15 rounded px-1.5 py-0.5 inline-block truncate max-w-full">
                            🏛️ {carte.institution || 'Non spécifiée'}
                          </div>
                        </div>
                        
                        {/* Infos carte */}
                        <div className="space-y-1.5">
                          <div>
                            <div className="text-white/40 text-[5px] uppercase tracking-wider">Titulaire</div>
                            <div className="text-white font-semibold text-[8px] tracking-wide truncate">
                              {carte.nom_etudiant?.toUpperCase() || currentUser.nom?.toUpperCase()} {carte.prenom_etudiant || currentUser.prenom}
                            </div>
                          </div>
                          
                          <div className="flex justify-between items-end">
                            <div>
                              <div className="text-white/40 text-[5px] uppercase tracking-wider">N° de carte</div>
                              <div className="text-white font-mono text-[7px] tracking-wider">
                                {carte.vddnn?.match(/.{1,4}/g)?.join(' ') || '**** **** ****'}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-white/40 text-[5px] uppercase tracking-wider">Expire fin</div>
                              <div className="text-white font-mono text-[7px]">
                                {new Date(carte.date_expiration).toLocaleDateString('fr-FR', { month: '2-digit', year: '2-digit' })}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Soldes */}
                        <div className="mt-2 pt-1.5 border-t border-white/20">
                          <div className="grid grid-cols-2 gap-1.5">
                            <div>
                              <div className="text-white/40 text-[5px] uppercase tracking-wider">Solde principal</div>
                              <div className="text-white font-bold text-[9px]">
                                {carte.compte_principal?.toLocaleString() || 0} <span className="text-[6px]">F</span>
                              </div>
                            </div>
                            <div>
                              <div className="text-white/40 text-[5px] uppercase tracking-wider">Solde CROU</div>
                              <div className="text-white font-bold text-[9px]">
                                {carte.compte_crou?.toLocaleString() || 0} <span className="text-[6px]">F</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Bande magnétique */}
                      <div className="absolute bottom-0 left-0 right-0 h-5 bg-blue-900/20"></div>
                    </div>
                    
                    {/* Code PIN */}
                    <div className="mt-1.5 flex justify-between items-center px-1">
                      <div className="text-[9px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                        <span className="font-semibold">🔑 PIN:</span>
                        <span className="font-mono ml-1 font-bold tracking-wider text-blue-700 text-[9px]">
                          {carte.code_pin || '••••••'}
                        </span>
                      </div>
                      <div className="text-[7px] text-gray-400">
                        {carte.statut_validation === 'valide' ? '✅ Validée' : '⏳ En attente'}
                      </div>
                    </div>
                  </div>

                  {/* FACE 2 - Verso - Format carte bancaire standard */}
                  <div 
                    ref={el => face2Refs.current[carte.id_carte] = el}
                    className="absolute inset-0 backface-hidden"
                    style={{ 
                      backfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)'
                    }}
                  >
                    <div className="bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg shadow-xl overflow-hidden" style={{ aspectRatio: '1.585 / 1', maxWidth: '340px', margin: '0 auto', width: '100%' }}>
                      {/* Bande magnétique verso */}
                      <div className="h-6 bg-black mt-2.5"></div>
                      
                      <div className="p-2.5">
                        {/* QR Code */}
                        <div className="text-center mb-1.5">
                          <div className="text-white/40 text-[6px] mb-0.5 uppercase tracking-wider">CODE PIN QR</div>
                          <div className="flex justify-center">
                            {qrImages[carte.id_carte] ? (
                              <img 
                                src={qrImages[carte.id_carte]} 
                                alt="QR Code PIN" 
                                className="w-14 h-14 bg-white p-1 rounded shadow-md"
                              />
                            ) : (
                              <div className="w-14 h-14 bg-gray-600 rounded animate-pulse"></div>
                            )}
                          </div>
                          <div className="text-white/30 text-[6px] mt-0.5">
                            Scannez pour payer
                          </div>
                        </div>
                        
                        {/* Institution verso */}
                        <div className="text-center mb-1.5">
                          <div className="text-white/30 text-[5px] uppercase tracking-wider">Institution</div>
                          <div className="text-white/50 text-[7px] font-mono">
                            {carte.institution || 'Non spécifiée'}
                          </div>
                        </div>
                        
                        {/* Signature */}
                        <div className="mt-1.5 pt-1.5 border-t border-white/20">
                          <div className="text-white/30 text-[6px] text-center">
                            Signature non autorisée
                          </div>
                          <div className="h-5 bg-white/5 rounded mt-0.5"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Boutons d'action */}
                <div className="mt-2 flex gap-1.5">
                  <button
                    onClick={() => toggleFace(carte.id_carte)}
                    className="flex-1 py-1 bg-gray-200 hover:bg-gray-300 rounded-lg transition flex items-center justify-center gap-1 text-gray-700 text-[10px] font-medium"
                  >
                    <span className="text-[10px]">🔄</span>
                    {estFace1 ? 'QR Code' : 'Infos'}
                  </button>
                  
                  <button
                    onClick={() => downloadFace(carte.id_carte, estFace1 ? 1 : 2, carteNom)}
                    className="py-1 px-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition text-white text-[10px] font-medium flex items-center gap-0.5"
                    title="Télécharger cette face"
                  >
                    <span>⬇️</span>
                  </button>
                  
                  <button
                    onClick={() => downloadBothFaces(carte.id_carte, carteNom)}
                    className="py-1 px-2 bg-green-600 hover:bg-green-700 rounded-lg transition text-white text-[10px] font-medium flex items-center gap-0.5"
                    title="Télécharger les deux faces"
                  >
                    <span>📥</span>
                  </button>
                </div>

                {/* Moyen de paiement associé */}
                {moyenPaiement && estFace1 && (
                  <div className="mt-1.5 p-1.5 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-1.5">
                      <div className="text-base">💳</div>
                      <div className="flex-1">
                        <p className="font-semibold text-green-800 text-[8px]">{moyenPaiement.type_paiement}</p>
                        <p className="text-[6px] text-green-600">📞 {moyenPaiement.source_qr}</p>
                        <p className="text-[5px] text-green-500">🏛️ {moyenPaiement.institution}</p>
                      </div>
                      {moyenPaiement.qr_image_url && (
                        <img 
                          src={moyenPaiement.qr_image_url} 
                          alt="QR Code" 
                          className="w-6 h-6 rounded border border-green-300"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}